import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Card, Button, Nav, Badge } from 'react-bootstrap';
import {
  Pencil,
  Trash,
  Plus,
  CheckLg,
  XLg,
  CaretUpFill,
  CaretDownFill,
  Check,
  X,
  ChevronRight
} from 'react-bootstrap-icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import { useProjects } from '../../contexts/ProjectContext';
import { useChat } from '../../contexts/ChatContext';
import { useContextEngine } from '../../contexts/ContextEngineContext';
import { useUsage } from '../../contexts/UsageContext';
import projectService from '../../services/projectService';
import contextService from '../../services/contextService';
import { Project, ProjectConversation, ProjectArtifact } from '../../types/project.types';
import { ContextSource, SignalCategory } from '../../types/context.types';
import { PLAN_LIMITS } from '../../types/usage.types';
import { formatFileSize } from '../../utils/fileUtils';
import ProjectModal from './ProjectModal';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import EmptyState from '../common/EmptyState';
import ProjectBrainModal from '../context/ProjectBrainModal';
import AddSourceModal from '../context/AddSourceModal';
import Footer from '../Footer';
import statsConversations from '../../assets/stats/stats-project-conversations.png';
import statsArtifacts from '../../assets/stats/stats-project-artifacts.png';
import statsDate from '../../assets/stats/stats-project-date.png';
import './ProjectDetail.css';

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { projects, updateProject, deleteProject } = useProjects();
  const { updateConversation, deleteConversation } = useChat();
  const {
    refreshSources,
    processOnboarding,
    processingContext,
    sources,
    signals,
    readinessScore,
    readinessBreakdown,
    deleteSource,
    deleteSignal,
    createManualSignal,
  } = useContextEngine();
  const { usageStats } = useUsage();
  const [project, setProject] = useState<Project | null>(null);
  const [conversations, setConversations] = useState<ProjectConversation[]>([]);
  const [artifacts, setArtifacts] = useState<ProjectArtifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'conversations' | 'artifacts' | 'files'>('conversations');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [editedTitle, setEditedTitle] = useState<string>('');
  const conversationEditInputRef = useRef<HTMLInputElement>(null);
  const conversationCursorPositionRef = useRef<number>(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<ProjectConversation | null>(null);
  const [editingArtifactId, setEditingArtifactId] = useState<string | null>(null);
  const [editedArtifactName, setEditedArtifactName] = useState<string>('');
  const artifactEditInputRef = useRef<HTMLInputElement>(null);
  const artifactCursorPositionRef = useRef<number>(0);
  const [showDeleteArtifactModal, setShowDeleteArtifactModal] = useState(false);
  const [artifactToDelete, setArtifactToDelete] = useState<ProjectArtifact | null>(null);
  const [conversationsSorting, setConversationsSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true }
  ]);
  const [artifactsSorting, setArtifactsSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true }
  ]);
  const [files, setFiles] = useState<ContextSource[]>([]);
  const [showDeleteFileModal, setShowDeleteFileModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<ContextSource | null>(null);
  const [filesSorting, setFilesSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true }
  ]);
  const [brainKey, setBrainKey] = useState(0);
  const [showAddSourceModal, setShowAddSourceModal] = useState(false);

  const planLimits = PLAN_LIMITS[usageStats?.plan ?? 'starter'];
  const sourceLimit = planLimits.sourcesPerProject;
  const maxFileSize = planLimits.maxFileSize;
  const sourceCount = sources.length;
  const hasUnlimitedSources = sourceLimit === null;
  const remainingSourceSlots = hasUnlimitedSources
    ? Number.MAX_SAFE_INTEGER
    : Math.max(0, sourceLimit - sourceCount);

  // Restore cursor position for conversation name editing
  useEffect(() => {
    if (conversationEditInputRef.current && editingConversationId) {
      conversationEditInputRef.current.setSelectionRange(conversationCursorPositionRef.current, conversationCursorPositionRef.current);
    }
  }, [editedTitle, editingConversationId]);

  // Restore cursor position for artifact name editing
  useEffect(() => {
    if (artifactEditInputRef.current && editingArtifactId) {
      artifactEditInputRef.current.setSelectionRange(artifactCursorPositionRef.current, artifactCursorPositionRef.current);
    }
  }, [editedArtifactName, editingArtifactId]);

  useEffect(() => {
    const currentProject = projects.find(p => p.id === projectId);
    if (currentProject) {
      setProject(currentProject);
      fetchConversations();
    } else if (projectId) {
      fetchProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projects]);

  const fetchProject = async () => {
    if (!projectId) return;

    try {
      const fetchedProject = await projectService.getProject(projectId);
      setProject(fetchedProject);
      fetchConversations();
    } catch (err: any) {
      console.error('Failed to load project:', err);
      setLoading(false);
    }
  };

  const fetchConversations = async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const projectConversations = await projectService.getProjectConversations(projectId);
      setConversations(projectConversations);
    } catch (err: any) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchArtifacts = async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const projectArtifacts = await projectService.getProjectArtifacts(projectId);
      setArtifacts(projectArtifacts);
    } catch (err: any) {
      console.error('Failed to load artifacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const projectFiles = await contextService.getSources(projectId);
      setFiles(projectFiles);
    } catch (err: any) {
      console.error('Failed to load files:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProject = async (data: any) => {
    if (!project) return;

    try {
      const updated = await updateProject(project.id, data);
      setProject(updated);
    } catch (err: any) {
      console.error('Failed to update project:', err);
    }
  };

  const handleDeleteProject = async () => {
    if (!project) return;

    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        await deleteProject(project.id);
        navigate('/projects');
      } catch (err: any) {
        console.error('Failed to delete project:', err);
      }
    }
  };

  const handleBrainComplete = async (result: {
    projectName: string;
    files: Array<{ fileName: string; fileType: string; fileSize: number; data: string }>;
    manualTags: Array<{ label: string; category: SignalCategory }>;
  }) => {
    if (!projectId) throw new Error('Missing projectId');
    const onboardingResult = await processOnboarding(projectId, result.files, result.manualTags);
    fetchFiles();
    return {
      sources: onboardingResult.sources,
      signals: onboardingResult.signals,
      readiness: onboardingResult.readiness,
    };
  };

  const handleBrainContinue = () => {
    setBrainKey((k) => k + 1);
  };

  const handleAddManualSignal = async (label: string, category: SignalCategory) => {
    if (!projectId) return;
    await createManualSignal(projectId, label, category);
  };

  const handleConversationClick = useCallback((conversationId: string) => {
    navigate(`/chat/${conversationId}`);
  }, [navigate]);

  const handleNewConversation = () => {
    navigate('/home', { state: { preselectedProjectId: projectId } });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Pagination
  const dataToDisplay = activeTab === 'conversations'
    ? conversations
    : activeTab === 'artifacts'
      ? artifacts
      : files;
  const totalPages = Math.ceil(dataToDisplay.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const paginatedConversations = useMemo(
    () => conversations.slice(startIndex, endIndex),
    [conversations, startIndex, endIndex]
  );

  const paginatedArtifacts = useMemo(
    () => artifacts.slice(startIndex, endIndex),
    [artifacts, startIndex, endIndex]
  );

  const paginatedFiles = useMemo(
    () => files.slice(startIndex, endIndex),
    [files, startIndex, endIndex]
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleStartEditDescription = () => {
    setEditedDescription(project?.description || '');
    setIsEditingDescription(true);
  };

  const handleSaveDescription = async () => {
    if (!project) return;

    try {
      const updated = await updateProject(project.id, { description: editedDescription });
      setProject(updated);
      setIsEditingDescription(false);
    } catch (err: any) {
      console.error('Failed to update description:', err);
    }
  };

  const handleCancelEditDescription = () => {
    setIsEditingDescription(false);
    setEditedDescription('');
  };

  const handleTabChange = (tab: 'conversations' | 'artifacts' | 'files') => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset pagination when switching tabs
    if (tab === 'artifacts' && artifacts.length === 0) {
      fetchArtifacts();
    }
    if (tab === 'files' && files.length === 0) {
      fetchFiles();
    }
  };

  const handleEditConversation = (e: React.MouseEvent, conversation: ProjectConversation) => {
    e.stopPropagation();
    setEditingConversationId(conversation.id);
    setEditedTitle(conversation.title || 'Untitled Conversation');
  };

  const handleSaveEdit = async (e: React.MouseEvent, conversation: ProjectConversation) => {
    e.stopPropagation();
    if (!editedTitle.trim()) {
      return;
    }
    try {
      await updateConversation(conversation.id, editedTitle.trim());
      // Update local state
      setConversations(prev => prev.map(conv =>
        conv.id === conversation.id ? { ...conv, title: editedTitle.trim() } : conv
      ));
      setEditingConversationId(null);
      setEditedTitle('');
    } catch (error) {
      console.error('Error updating conversation:', error);
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingConversationId(null);
    setEditedTitle('');
  };

  const handleDeleteConversation = (e: React.MouseEvent, conversation: ProjectConversation) => {
    e.stopPropagation();
    setConversationToDelete(conversation);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (conversationToDelete) {
      try {
        await deleteConversation(conversationToDelete.id);
        // Update local state
        setConversations(prev => prev.filter(conv => conv.id !== conversationToDelete.id));
        setShowDeleteModal(false);
        setConversationToDelete(null);
      } catch (error) {
        console.error('Error deleting conversation:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setConversationToDelete(null);
  };

  const handleEditArtifact = (e: React.MouseEvent, artifact: ProjectArtifact) => {
    e.stopPropagation();
    setEditingArtifactId(artifact.id);
    setEditedArtifactName(artifact.name || 'Untitled Artifact');
  };

  const handleSaveArtifactEdit = async (e: React.MouseEvent, artifact: ProjectArtifact) => {
    e.stopPropagation();
    if (!editedArtifactName.trim()) {
      return;
    }
    try {
      await projectService.updateArtifact(artifact.id, { name: editedArtifactName.trim() });
      // Update local state
      setArtifacts(prev => prev.map(art =>
        art.id === artifact.id ? { ...art, name: editedArtifactName.trim() } : art
      ));
      setEditingArtifactId(null);
      setEditedArtifactName('');
    } catch (error) {
      console.error('Error updating artifact:', error);
    }
  };

  const handleCancelArtifactEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingArtifactId(null);
    setEditedArtifactName('');
  };

  const handleDeleteArtifact = (e: React.MouseEvent, artifact: ProjectArtifact) => {
    e.stopPropagation();
    setArtifactToDelete(artifact);
    setShowDeleteArtifactModal(true);
  };

  const handleConfirmArtifactDelete = async () => {
    if (artifactToDelete && projectId) {
      try {
        // Delete the artifact - the backend should handle removing references from conversations
        await projectService.deleteArtifact(projectId, artifactToDelete.id);
        // Update local state
        setArtifacts(prev => prev.filter(art => art.id !== artifactToDelete.id));
        setShowDeleteArtifactModal(false);
        setArtifactToDelete(null);
      } catch (error) {
        console.error('Error deleting artifact:', error);
      }
    }
  };

  const handleCancelArtifactDelete = () => {
    setShowDeleteArtifactModal(false);
    setArtifactToDelete(null);
  };

  const handleConfirmFileDelete = async () => {
    if (fileToDelete) {
      try {
        await contextService.deleteSource(fileToDelete.id);
        setFiles(prev => prev.filter(f => f.id !== fileToDelete.id));
        setShowDeleteFileModal(false);
        setFileToDelete(null);
        // Sync with ContextEngineContext so sidebar updates
        refreshSources();
      } catch (error) {
        console.error('Error deleting file:', error);
      }
    }
  };

  const handleCancelFileDelete = () => {
    setShowDeleteFileModal(false);
    setFileToDelete(null);
  };

  // Conversations columns
  const conversationsColumns: ColumnDef<ProjectConversation>[] = useMemo(() => [
    {
      accessorKey: 'createdAt',
      header: 'Date',
      cell: ({ getValue }) => (
        <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '14px', color: '#191919' }}>
          {formatDate(getValue() as string)}
        </span>
      ),
    },
    {
      accessorKey: 'title',
      header: 'Conversation Name',
      cell: ({ row }) => {
        const isEditing = editingConversationId === row.original.id;

        if (isEditing) {
          return (
            <div className="conversation-name-column" onClick={(e) => e.stopPropagation()}>
              <input
                ref={conversationEditInputRef}
                type="text"
                className="form-control form-control-sm"
                value={editedTitle}
                onChange={(e) => {
                  conversationCursorPositionRef.current = e.target.selectionStart || 0;
                  setEditedTitle(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveEdit(e as any, row.original);
                  } else if (e.key === 'Escape') {
                    handleCancelEdit(e as any);
                  }
                }}
                onClick={(e) => {
                  conversationCursorPositionRef.current = (e.target as HTMLInputElement).selectionStart || 0;
                }}
                onKeyUp={(e) => {
                  conversationCursorPositionRef.current = (e.target as HTMLInputElement).selectionStart || 0;
                }}
                style={{
                  fontFamily: 'Neue Haas Grotesk Display Pro, sans-serif',
                  fontWeight: 500,
                  fontSize: '16px'
                }}
                autoFocus
              />
            </div>
          );
        }

        return (
          <div
            className="conversation-name-column"
            onClick={() => handleConversationClick(row.original.id)}
            style={{ cursor: 'pointer' }}
          >
            <span
              className="conversation-title"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: 600,
                color: '#474920'
              }}
            >
              {row.original.title || `Conversation ${row.original.id.slice(-6)}`}
            </span>
            {row.original.lastMessage && row.original.lastMessage.includes('meal plan') && (
              <Badge bg="danger" className="ms-2 hover-badge">HOVER</Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'artifactCount',
      header: 'Artifact Count',
      cell: ({ getValue }) => (
        <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '14px', color: '#191919' }}>
          {(getValue() as number) || 0}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const isEditing = editingConversationId === row.original.id;

        if (isEditing) {
          return (
            <div className="actions-column d-flex gap-2 edit-container" onClick={(e) => e.stopPropagation()}>
              <button
                className="btn btn-link p-0 edit-save"
                style={{ border: 'none', background: 'none', color: '#28a745' }}
                aria-label="Save changes"
                onClick={(e) => handleSaveEdit(e, row.original)}
              >
                <Check size={24} />
              </button>
              <button
                className="btn btn-link p-0 edit-cancel"
                style={{ border: 'none', background: 'none', color: '#dc3545' }}
                aria-label="Cancel editing"
                onClick={handleCancelEdit}
              >
                <X size={24} />
              </button>
            </div>
          );
        }

        return (
          <div className="actions-column d-flex gap-2">
            <button
              className="btn btn-link text-muted p-0"
              style={{ border: 'none', background: 'none' }}
              onClick={(e) => handleEditConversation(e, row.original)}
              aria-label="Edit conversation"
            >
              <Pencil size={16} />
            </button>
            <button
              className="btn btn-link text-danger p-0"
              style={{ border: 'none', background: 'none' }}
              onClick={(e) => handleDeleteConversation(e, row.original)}
              aria-label="Delete conversation"
            >
              <Trash size={16} />
            </button>
          </div>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [handleConversationClick, editingConversationId, editedTitle]);

  // Artifacts columns
  const artifactsColumns: ColumnDef<ProjectArtifact>[] = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Artifact Name',
      cell: ({ row }) => {
        const isEditing = editingArtifactId === row.original.id;

        if (isEditing) {
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <input
                ref={artifactEditInputRef}
                type="text"
                className="form-control form-control-sm"
                value={editedArtifactName}
                onChange={(e) => {
                  artifactCursorPositionRef.current = e.target.selectionStart || 0;
                  setEditedArtifactName(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveArtifactEdit(e as any, row.original);
                  } else if (e.key === 'Escape') {
                    handleCancelArtifactEdit(e as any);
                  }
                }}
                onClick={(e) => {
                  artifactCursorPositionRef.current = (e.target as HTMLInputElement).selectionStart || 0;
                }}
                onKeyUp={(e) => {
                  artifactCursorPositionRef.current = (e.target as HTMLInputElement).selectionStart || 0;
                }}
                style={{
                  fontFamily: 'Neue Haas Grotesk Display Pro, sans-serif',
                  fontWeight: 500,
                  fontSize: '16px'
                }}
                autoFocus
              />
            </div>
          );
        }

        return (
          <span
            className="conversation-title"
            style={{
              textDecoration: 'underline',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '14px',
              fontWeight: 600,
              color: '#474920'
            }}
          >
            {row.original.name || 'Untitled Artifact'}
          </span>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Date Created',
      cell: ({ getValue }) => (
        <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '14px' }}>
          {formatDate(getValue() as string)}
        </span>
      ),
    },
    {
      accessorKey: 'lastEdited',
      header: 'Last Edited',
      cell: ({ getValue }) => (
        <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '14px' }}>
          {formatDate(getValue() as string)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const isEditing = editingArtifactId === row.original.id;

        if (isEditing) {
          return (
            <div className="actions-column d-flex gap-2 edit-container" onClick={(e) => e.stopPropagation()}>
              <button
                className="btn btn-link p-0 edit-save"
                style={{ border: 'none', background: 'none', color: '#28a745' }}
                aria-label="Save changes"
                onClick={(e) => handleSaveArtifactEdit(e, row.original)}
              >
                <Check size={24} />
              </button>
              <button
                className="btn btn-link p-0 edit-cancel"
                style={{ border: 'none', background: 'none', color: '#dc3545' }}
                aria-label="Cancel editing"
                onClick={handleCancelArtifactEdit}
              >
                <X size={24} />
              </button>
            </div>
          );
        }

        return (
          <div className="actions-column d-flex gap-2">
            <button
              className="btn btn-link text-muted p-0"
              style={{ border: 'none', background: 'none' }}
              onClick={(e) => handleEditArtifact(e, row.original)}
              aria-label="Edit artifact"
              title="Edit artifact"
            >
              <Pencil size={16} />
            </button>
            <button
              className="btn btn-link text-danger p-0"
              style={{ border: 'none', background: 'none' }}
              onClick={(e) => handleDeleteArtifact(e, row.original)}
              aria-label="Delete artifact"
              title="Delete artifact"
            >
              <Trash size={16} />
            </button>
          </div>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [editingArtifactId, editedArtifactName]);

  // Conversations table
  const conversationsTable = useReactTable({
    data: paginatedConversations,
    columns: conversationsColumns,
    state: {
      sorting: conversationsSorting,
    },
    onSortingChange: setConversationsSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  // Artifacts table
  const artifactsTable = useReactTable({
    data: paginatedArtifacts,
    columns: artifactsColumns,
    state: {
      sorting: artifactsSorting,
    },
    onSortingChange: setArtifactsSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  // Files columns
  const filesColumns: ColumnDef<ContextSource>[] = useMemo(() => [
    {
      accessorKey: 'fileName',
      header: 'Filename',
      cell: ({ getValue }) => (
        <span style={{
          fontFamily: 'Montserrat, sans-serif',
          fontSize: '14px',
          fontWeight: 600,
          color: '#474920'
        }}>
          {getValue() as string}
        </span>
      ),
    },
    {
      accessorKey: 'fileSize',
      header: 'File Size',
      cell: ({ getValue }) => (
        <span style={{
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 400,
          fontSize: '14px',
          color: '#191919'
        }}>
          {formatFileSize(getValue() as number)}
        </span>
      ),
    },
    {
      accessorKey: 'fileType',
      header: 'File Type',
      cell: ({ getValue }) => (
        <span style={{
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 400,
          fontSize: '14px',
          color: '#191919'
        }}>
          {getValue() as string}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="actions-column d-flex gap-2">
          <button
            className="btn btn-link text-danger p-0"
            style={{ border: 'none', background: 'none' }}
            onClick={(e) => {
              e.stopPropagation();
              setFileToDelete(row.original);
              setShowDeleteFileModal(true);
            }}
            aria-label="Delete file"
            title="Delete file"
          >
            <Trash size={16} />
          </button>
        </div>
      ),
    },
  ], []);

  // Files table
  const filesTable = useReactTable({
    data: paginatedFiles,
    columns: filesColumns,
    state: {
      sorting: filesSorting,
    },
    onSortingChange: setFilesSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  if (loading) {
    return (
      <Container className="py-4">
        <div className="text-center">Loading...</div>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container className="py-4">
        <div className="text-center text-danger">Project not found</div>
      </Container>
    );
  }

  return (
    <div className="project-detail-page">
      <Container fluid className="px-5 py-4">
        <div className="project-breadcrumbs">
          <Link to="/home" className="breadcrumb-link">Home</Link>
          <ChevronRight className="breadcrumb-separator" />
          <Link to="/projects" className="breadcrumb-link">Projects</Link>
          <ChevronRight className="breadcrumb-separator" />
          <span className="breadcrumb-current">
            {project.name}
          </span>
        </div>
        <div className="project-header mb-4">
          <h1>{project.name}</h1>
          <div className="project-description-row">
            {isEditingDescription ? (
              <>
                <input
                  type="text"
                  className="description-edit-input"
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  autoFocus
                  placeholder="Enter project description"
                />
                <button
                  className="edit-action-btn save-btn"
                  onClick={handleSaveDescription}
                  aria-label="Save description"
                >
                  <CheckLg size={16} />
                </button>
                <button
                  className="edit-action-btn cancel-btn"
                  onClick={handleCancelEditDescription}
                  aria-label="Cancel edit"
                >
                  <XLg size={16} />
                </button>
              </>
            ) : (
              <>
                <p className="project-description" style={{ textTransform: 'uppercase' }}>
                  {project.description || 'No description provided'}
                </p>
                <button
                  className="edit-description-btn"
                  onClick={handleStartEditDescription}
                  aria-label="Edit description"
                >
                  <Pencil size={16} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-cards-container mb-4">
          <Card className="stat-card">
            <Card.Body>
              <div className="stat-icon mb-2">
                <img src={statsConversations} alt="Conversations" className="stat-image" />
              </div>
              <h3 className="stat-number">{project.conversationCount || 0}</h3>
              <div className="stat-label">CONVERSATIONS</div>
            </Card.Body>
          </Card>
          <Card className="stat-card">
            <Card.Body>
              <div className="stat-icon mb-2">
                <img src={statsArtifacts} alt="Artifacts" className="stat-image" />
              </div>
              <h3 className="stat-number">{project.artifactCount || 0}</h3>
              <div className="stat-label">ARTIFACTS</div>
            </Card.Body>
          </Card>
          <Card className="stat-card">
            <Card.Body>
              <div className="stat-icon mb-2">
                <img src={statsDate} alt="Date Created" className="stat-image" />
              </div>
              <h3 className="stat-number">{formatDate(project.createdAt)}</h3>
              <div className="stat-label">DATE CREATED</div>
            </Card.Body>
          </Card>
        </div>

        {/* Tabs and Action Button */}
        <div className="tabs-section mb-4">
          <Nav variant="tabs" className="project-tabs">
            <Nav.Item>
              <Nav.Link
                active={activeTab === 'conversations'}
                onClick={() => handleTabChange('conversations')}
                className={activeTab === 'conversations' ? 'active' : ''}
              >
                CONVERSATIONS
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={activeTab === 'artifacts'}
                onClick={() => handleTabChange('artifacts')}
                className={activeTab === 'artifacts' ? 'active' : ''}
              >
                ARTIFACTS
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={activeTab === 'files'}
                onClick={() => handleTabChange('files')}
                className={activeTab === 'files' ? 'active' : ''}
              >
                FILES
              </Nav.Link>
            </Nav.Item>
          </Nav>
          {activeTab !== 'files' && (
            <Button
              variant="success"
              className="new-conversation-btn"
              onClick={handleNewConversation}
            >
              <Plus size={20} className="me-2" />
              START NEW CONVERSATION
            </Button>
          )}
        </div>

        {/* Conversations Table */}
        {activeTab === 'conversations' && (
          <>
            {paginatedConversations.length === 0 ? (
              <div className="d-flex justify-content-center">
                <EmptyState
                  type="conversations"
                  onButtonClick={handleNewConversation}
                />
              </div>
            ) : (
              <>
                <div className="conversation-table-container">
                  <table className="conversation-table">
                    <thead>
                      {conversationsTable.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th
                              key={header.id}
                              colSpan={header.colSpan}
                              style={{
                                cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                fontFamily: 'Montserrat, sans-serif',
                                fontSize: '14px',
                                fontWeight: 700
                              }}
                            >
                              {header.isPlaceholder ? null : (
                                <div
                                  className="d-flex align-items-center"
                                  onClick={header.column.getToggleSortingHandler()}
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                  {{
                                    asc: <CaretUpFill className="ms-2" size={12} />,
                                    desc: <CaretDownFill className="ms-2" size={12} />,
                                  }[header.column.getIsSorted() as string] ?? (
                                    header.column.getCanSort() ? (
                                      <span className="ms-2" style={{ opacity: 0.3 }}>
                                        <CaretUpFill size={12} />
                                      </span>
                                    ) : null
                                  )}
                                </div>
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {conversationsTable.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-end mt-3 mb-3">
                    <div className="pagination-controls d-flex align-items-center gap-2">
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <span className="pagination-number active">{currentPage}</span>
                      {currentPage < totalPages && (
                        <span className="pagination-number">{currentPage + 1}</span>
                      )}
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Artifacts Tab */}
        {activeTab === 'artifacts' && (
          <>
            {paginatedArtifacts.length === 0 ? (
              <div className="d-flex justify-content-center py-5">
                <EmptyState
                  type="artifacts"
                  onButtonClick={handleNewConversation}
                />
              </div>
            ) : (
              <>
                <div className="conversation-table-container">
                  <table className="conversation-table">
                    <thead>
                      {artifactsTable.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th
                              key={header.id}
                              colSpan={header.colSpan}
                              style={{
                                cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                fontFamily: 'Montserrat, sans-serif',
                                fontSize: '14px',
                                fontWeight: 700
                              }}
                            >
                              {header.isPlaceholder ? null : (
                                <div
                                  className="d-flex align-items-center"
                                  onClick={header.column.getToggleSortingHandler()}
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                  {{
                                    asc: <CaretUpFill className="ms-2" size={12} />,
                                    desc: <CaretDownFill className="ms-2" size={12} />,
                                  }[header.column.getIsSorted() as string] ?? (
                                    header.column.getCanSort() ? (
                                      <span className="ms-2" style={{ opacity: 0.3 }}>
                                        <CaretUpFill size={12} />
                                      </span>
                                    ) : null
                                  )}
                                </div>
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {artifactsTable.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-end mt-3 mb-3">
                    <div className="pagination-controls d-flex align-items-center gap-2">
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <span className="pagination-number active">{currentPage}</span>
                      {currentPage < totalPages && (
                        <span className="pagination-number">{currentPage + 1}</span>
                      )}
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Files Tab */}
        {activeTab === 'files' && (
          <>
            {paginatedFiles.length === 0 ? (
              <div className="d-flex justify-content-center py-5">
                <EmptyState
                  type="files"
                  onButtonClick={handleNewConversation}
                />
              </div>
            ) : (
              <>
                <div className="conversation-table-container">
                  <table className="conversation-table">
                    <thead>
                      {filesTable.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th
                              key={header.id}
                              colSpan={header.colSpan}
                              style={{
                                cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                fontFamily: 'Montserrat, sans-serif',
                                fontSize: '14px',
                                fontWeight: 700
                              }}
                            >
                              {header.isPlaceholder ? null : (
                                <div
                                  className="d-flex align-items-center"
                                  onClick={header.column.getToggleSortingHandler()}
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                  {{
                                    asc: <CaretUpFill className="ms-2" size={12} />,
                                    desc: <CaretDownFill className="ms-2" size={12} />,
                                  }[header.column.getIsSorted() as string] ?? (
                                    header.column.getCanSort() ? (
                                      <span className="ms-2" style={{ opacity: 0.3 }}>
                                        <CaretUpFill size={12} />
                                      </span>
                                    ) : null
                                  )}
                                </div>
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {filesTable.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-end mt-3 mb-3">
                    <div className="pagination-controls d-flex align-items-center gap-2">
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <span className="pagination-number active">{currentPage}</span>
                      {currentPage < totalPages && (
                        <span className="pagination-number">{currentPage + 1}</span>
                      )}
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        <div className="project-brain-section-embed mt-5">
          <ProjectBrainModal
            key={brainKey}
            onComplete={handleBrainComplete}
            onContinue={handleBrainContinue}
            processing={processingContext}
            continueButtonText="Done"
            existingContext={{
              sources,
              signals,
              readinessScore,
              readinessBreakdown,
            }}
            onDeleteSource={deleteSource}
            onDeleteSignal={deleteSignal}
            onAddManualSignal={handleAddManualSignal}
            onAddSource={() => setShowAddSourceModal(true)}
          />
        </div>

        {/* Delete Project Button */}
        <div className="delete-project-section mt-5">
          <Button
            variant="link"
            className="delete-project-btn"
            onClick={handleDeleteProject}
          >
            <Trash className="me-2" />
            DELETE PROJECT
          </Button>
        </div>
      </Container>

      <ProjectModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        onSave={handleSaveProject}
        project={project}
        title="Edit Project"
      />

      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        itemType="Conversation"
        artifactCount={conversationToDelete?.artifactCount || 0}
      />

      <DeleteConfirmationModal
        show={showDeleteArtifactModal}
        onHide={handleCancelArtifactDelete}
        onConfirm={handleConfirmArtifactDelete}
        itemType="Artifact"
        artifactCount={0}
      />

      <DeleteConfirmationModal
        show={showDeleteFileModal}
        onHide={handleCancelFileDelete}
        onConfirm={handleConfirmFileDelete}
        itemType="File"
        artifactCount={0}
      />

      <AddSourceModal
        show={showAddSourceModal}
        onHide={() => setShowAddSourceModal(false)}
        remainingSlots={remainingSourceSlots}
        maxFileSize={maxFileSize}
        currentCount={sourceCount}
        limit={sourceLimit}
      />

      <Footer />
    </div>
  );
};

export default ProjectDetail;
